import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { BASE_URL } from "../../utils/constants";
import { getDefaultHeaders } from "./getDefaultHeaders";
import i18n from "../../i18n";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import { getCookie } from "../../utils/token";
import { IResponse } from "../types";
import { logoutSuccess, showUnauthorizedModal, updateToken } from "../actions/auth";
import { IAuthResponse } from "../types/auth.types";
import { loadAppConfig, resolveApiBaseUrl } from "../../utils/config";
import {
  showFeatureRestrictionPopup,
  showFeatureQuotaRestrictionPopup,
  showRateLimitRestrictionPopup,
  showResourceRestrictionPopup,
} from "../slices/accessControl.slice";

export let BASE_URL_DYNAMIC = BASE_URL;

try {
  const cfg = await loadAppConfig();
  BASE_URL_DYNAMIC = resolveApiBaseUrl(cfg) || BASE_URL;
} catch (error) {
  // console.error("Error loading app config:", error);
}

// Create our baseQuery instance

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL_DYNAMIC,
  // Send the bpreven.auth HttpOnly cookie on cross-origin API calls and
  // honor Set-Cookie from the login response. Required for <img>/<audio>/
  // <video> src attributes that hit FileProxyController via the cookie
  // auth scheme. Safe: CORS allow-list is explicit (no wildcard) and the
  // cookie scheme is only accepted on the read-only FileProxyController.
  credentials: "include",
  prepareHeaders: (headers, endpoints) => {
    if (headers.has("X-Is-File-Upload")) {
      headers.delete("X-Is-File-Upload");
      const state = (endpoints?.getState?.() ?? {}) as {
        user?: { accessToken?: string };
      };
      const accessToken: string | undefined =
        getCookie("accessToken") || state.user?.accessToken || undefined;
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }
      // Add Accept-Language header for special file upload case too
      const currentLanguage = i18n.language || 'he';
      headers.set("Accept-Language", currentLanguage);
      headers.set("X-App-Name", 'admin');
      return headers;
    }
    const state = (endpoints?.getState?.() ?? {}) as {
      user?: { accessToken?: string };
    };
    const accessToken: string | undefined =
      getCookie("accessToken") || state.user?.accessToken || undefined;
    return getDefaultHeaders(headers, endpoints, accessToken);
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const isFormData = typeof args === "object" && args.body instanceof FormData;

  if (isFormData) {
    const fileUploadQuery = fetchBaseQuery({
      baseUrl: BASE_URL_DYNAMIC,
      credentials: "include",
      prepareHeaders: (headers, { getState }) => {
        const accessToken: string | undefined = getCookie("accessToken");
        if (accessToken) {
          headers.set("Authorization", `Bearer ${accessToken}`);
        }
        // Add Accept-Language header for file uploads too
        const currentLanguage = i18n.language || 'he';
        headers.set("Accept-Language", currentLanguage);
        headers.set("X-App-Name", 'admin');
        return headers;
      },
    });

    const fileResult = await fileUploadQuery(args, api, extraOptions);
    if (
      fileResult.error?.status === 403 &&
      (fileResult.error.data as { error?: string })?.error === "resource_quota_exceeded"
    ) {
      api.dispatch(showResourceRestrictionPopup("resource_quota_exceeded"));
      return { data: null };
    }
    return fileResult;
  }

  const refreshToken = getCookie("refreshToken");
  let result = await baseQuery(args, api, extraOptions);
  if (result.error?.status === 429) {
    const body = result.error.data as {
      error?: string;
      feature?: string;
      scope?: string | null;
      limit?: number;
      used?: number;
      resetsAt?: string | null;
    };
    if (body?.error === "feature_usage_quota_exceeded") {
      api.dispatch(
        showFeatureQuotaRestrictionPopup({
          feature: body.feature ?? "",
          scope: body.scope ?? null,
          limit: body.limit ?? 0,
          used: body.used ?? 0,
          resetsAt: body.resetsAt ?? null,
        })
      );
      return { data: null };
    } else if (
      // The backend renamed this refusal (credit-pricing-model.md note 6):
      // ai_token_quota_exceeded → credit_balance_exceeded. Both are accepted so the
      // popup works against an older deployment too.
      body?.error === "credit_balance_exceeded" ||
      body?.error === "ai_token_quota_exceeded"
    ) {
      api.dispatch(
        showFeatureQuotaRestrictionPopup({
          feature: "ai_tokens",
          scope: body.scope ?? null,
          limit: body.limit ?? 0,
          used: body.used ?? 0,
          resetsAt: body.resetsAt ?? null,
        })
      );
      return { data: null };
    } else if (api.endpoint !== "buyFeatureCredits") {
      // Rejected by the IP rate limiter, which answers with plain text and no error code.
      // The error is still returned (not swallowed) so callers keep their own handling —
      // buyFeatureCredits has a dedicated throttled outcome, so it gets no popup.
      api.dispatch(showRateLimitRestrictionPopup());
    }
  }
  if (result.error?.status === 401 || result.error?.status === 403) {
    const errorCode = (result.error.data as { error?: string })?.error;

    if (
      result.error.status === 403 &&
      errorCode === "feature_not_in_plan"
    ) {
      api.dispatch(showFeatureRestrictionPopup(errorCode));
      const { userApi } = await import("./user.api");
      api.dispatch(
        userApi.endpoints.getUserEntitlements.initiate(
          undefined,
          { forceRefetch: true }
        )
      );
      return { data: null };
    } else if (
      result.error.status === 403 &&
      errorCode === "resource_quota_exceeded"
    ) {
      api.dispatch(showResourceRestrictionPopup(errorCode));
      return { data: null };
    } else {
      api.dispatch(showUnauthorizedModal(true));
    }
  }
  if (
    refreshToken &&
    result.error &&
    (result.error.status === 401 ||
      (result.error.status === 403 &&
        (result.error.data as IResponse).message === "jwt expired"))
  ) {
    const refreshResult = await baseQuery(
      {
        url: `/token`,
        method: "POST",
        body: {
          token: refreshToken,
        },
      },
      api,
      extraOptions
    );
    if (refreshResult.data) {
      api.dispatch(updateToken(refreshResult.data as IAuthResponse));
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logoutSuccess());
    }
  }
  
  return result;
};
